import { PublicationStatusSchema } from "../solo-cms/publication-status";
import { z } from "zod";

const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a 6-digit hex color like #RRGGBB");

// Reward label for amounts on Flow. Glyph symbols prefix ("$1,000"); word symbols suffix ("1,000 USDC").
const RewardSymbolSchema = z.string().trim().min(1).max(16).default("$");

// Series slug: competitions sharing it roll up on the Flow index. `null` = standalone.
export const GROUP_SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const GroupSlugSchema = z.string().regex(GROUP_SLUG_REGEX, "groupSlug must be kebab-case").nullable().default(null);

// ====== Volume tier (display-only unlock steps) ======

/**
 * Per-rank reward distribution inside a single tier. Each bracket covers
 * an inclusive `[rankFrom, rankTo]` range and gives the traders inside
 * that range the same `rewardUsd`. Optional — a tier with an empty
 * bracket list simply does not show per-rank rewards in the leaderboard.
 */
export const VolumeTierBracketSchema = z
  .object({
    rankFrom: z.number().int().min(1),
    rankTo: z.number().int().min(1),
    rewardUsd: z.number().nonnegative(),
  })
  .refine((b) => b.rankTo >= b.rankFrom, { error: "rankTo must be >= rankFrom" });
export type VolumeTierBracket = z.infer<typeof VolumeTierBracketSchema>;

/**
 * A single volume tier. When the competition's TOTAL traded volume
 * reaches `volumeUsd`, `reward` is treated as unlocked by the UI. The
 * legacy `reward` display string stays as the headline "unlocked prize
 * pool" chip; `brackets` is the new per-rank distribution used by the
 * leaderboard table. Rewards are still paid out manually off-platform —
 * the data here is purely for display.
 */
export const VolumeTierSchema = z.object({
  volumeUsd: z.number().nonnegative(),
  reward: z.string().min(1),
  brackets: z.array(VolumeTierBracketSchema).default([]),
});
export type VolumeTier = z.infer<typeof VolumeTierSchema>;

// ====== Point-boost tier (display-only, per-user volume multiplier) ======

/**
 * A single point-boost tier. When a trader's OWN volume in the competition
 * window reaches `volumeUsd`, the UI shows `multiplier` as their unlocked
 * point boost (e.g. `2` renders as "200%"). Unlike `VolumeTier`, this axis
 * is keyed to per-user volume, not total platform volume or leaderboard
 * rank. Display-only — the points themselves are awarded off-platform.
 */
export const PointBoostTierSchema = z.object({
  volumeUsd: z.number().nonnegative(),
  multiplier: z.number().positive(),
});
export type PointBoostTier = z.infer<typeof PointBoostTierSchema>;

// ====== Response DTO (read) ======

export const CedeFlowTradeAndEarnCompetitionDtoSchema = z.object({
  id: z.uuid(),
  slug: z.string().min(1),
  title: z.string().min(1),
  description: z.string(),
  exchangeId: z.string().min(1),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  /**
   * Optional display-only window override. When set, Flow surfaces
   * (card, detail-page hero, banner) render this date range instead
   * of the actual `[startDate, endDate]`. Useful for daily-reset
   * series: each one-day competition can advertise the same "May 1 –
   * May 31" span without us inventing a group entity. Scoring still
   * uses the real `[startDate, endDate]`.
   */
  displayStartDate: z.coerce.date().nullable().optional(),
  displayEndDate: z.coerce.date().nullable().optional(),
  prizePool: z.string(),
  rewardSymbol: RewardSymbolSchema,
  coverImageFileId: z.uuid().nullable().optional(),
  coverImageUrl: z.string().nullable().optional(),
  volumeTiers: z.array(VolumeTierSchema).default([]),
  pointBoostTiers: z.array(PointBoostTierSchema).default([]),
  publicationStatus: PublicationStatusSchema,
  showBannerOnFlow: z.boolean().default(false),
  bannerColor: HexColorSchema.nullable().optional(),
  bannerExtraReward: z.string().nullable().optional(),
  // Base-asset whitelist (upper-case). `[]` = no filter; all pairs count. Applied when fills are persisted.
  allowedSymbols: z.array(z.string().min(1)).default([]),
  groupSlug: GroupSlugSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type CedeFlowTradeAndEarnCompetitionDto = z.infer<typeof CedeFlowTradeAndEarnCompetitionDtoSchema>;

// ====== Save DTOs (write) ======

const baseCedeFlowTradeAndEarnCompetitionFields = {
  slug: z.string().min(1),
  title: z.string().min(1),
  description: z.string(),
  exchangeId: z.string().min(1),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  displayStartDate: z.coerce.date().nullable().optional(),
  displayEndDate: z.coerce.date().nullable().optional(),
  prizePool: z.string(),
  rewardSymbol: RewardSymbolSchema,
  coverImageFileId: z.uuid().nullable().optional(),
  volumeTiers: z.array(VolumeTierSchema).default([]),
  pointBoostTiers: z.array(PointBoostTierSchema).default([]),
  publicationStatus: PublicationStatusSchema,
  showBannerOnFlow: z.boolean().default(false),
  bannerColor: HexColorSchema.nullable().optional(),
  bannerExtraReward: z.string().nullable().optional(),
  allowedSymbols: z.array(z.string().min(1)).default([]),
  groupSlug: GroupSlugSchema,
} as const;

const enforceDateWindow = (value: { startDate: Date; endDate: Date }, ctx: z.RefinementCtx): void => {
  if (value.endDate.getTime() <= value.startDate.getTime()) {
    ctx.addIssue({
      code: "custom",
      message: "endDate must be after startDate",
      path: ["endDate"],
    });
  }
};

const enforceDisplayDateWindow = (
  value: { displayStartDate?: Date | null; displayEndDate?: Date | null },
  ctx: z.RefinementCtx,
): void => {
  const start = value.displayStartDate ?? null;
  const end = value.displayEndDate ?? null;
  // Display dates are an all-or-nothing override: either both are set,
  // or neither is. A single half-set value leaves the Flow UI guessing
  // which to use, so reject it at the schema boundary.
  if (start && !end) {
    ctx.addIssue({
      code: "custom",
      message: "displayEndDate is required when displayStartDate is set",
      path: ["displayEndDate"],
    });
  }
  if (end && !start) {
    ctx.addIssue({
      code: "custom",
      message: "displayStartDate is required when displayEndDate is set",
      path: ["displayStartDate"],
    });
  }
  if (start && end && end.getTime() <= start.getTime()) {
    ctx.addIssue({
      code: "custom",
      message: "displayEndDate must be after displayStartDate",
      path: ["displayEndDate"],
    });
  }
};

const enforceBannerColor = (
  value: { showBannerOnFlow?: boolean; bannerColor?: string | null },
  ctx: z.RefinementCtx,
): void => {
  if (value.showBannerOnFlow && value.bannerColor == null) {
    ctx.addIssue({
      code: "custom",
      message: "bannerColor is required when showBannerOnFlow is true",
      path: ["bannerColor"],
    });
  }
};

// Trim/uppercase/dedupe allowed-symbols in place (`[]` is the valid "no filter" sentinel).
const normalizeAllowedSymbols = (value: { allowedSymbols?: readonly string[] }): void => {
  if (!value.allowedSymbols) return;
  const seen = new Set<string>();
  const cleaned: string[] = [];
  for (const raw of value.allowedSymbols) {
    const sym = raw.trim().toUpperCase();
    if (sym.length === 0) continue;
    if (seen.has(sym)) continue;
    seen.add(sym);
    cleaned.push(sym);
  }
  (value as { allowedSymbols: string[] }).allowedSymbols = cleaned;
};

const enforceSaveInvariants = (
  value: {
    startDate: Date;
    endDate: Date;
    displayStartDate?: Date | null;
    displayEndDate?: Date | null;
    showBannerOnFlow?: boolean;
    bannerColor?: string | null;
    allowedSymbols?: readonly string[];
  },
  ctx: z.RefinementCtx,
): void => {
  enforceDateWindow(value, ctx);
  enforceDisplayDateWindow(value, ctx);
  enforceBannerColor(value, ctx);
  normalizeAllowedSymbols(value);
};

// Create branch — `id` MUST be absent. `z.strictObject` rejects payloads that
// carry an `id` key with a malformed value, so the `Save` union below
// can't silently fall through from a failed `Update` validation into a
// successful `Create` parse (which would otherwise strip the bad `id`
// and turn a botched edit into an unintended insert).
export const CreateCedeFlowTradeAndEarnCompetitionDtoSchema = z
  .strictObject(baseCedeFlowTradeAndEarnCompetitionFields)
  .superRefine(enforceSaveInvariants);
export type CreateCedeFlowTradeAndEarnCompetitionDto = z.infer<typeof CreateCedeFlowTradeAndEarnCompetitionDtoSchema>;

export const UpdateCedeFlowTradeAndEarnCompetitionDtoSchema = z
  .object({ id: z.uuid(), ...baseCedeFlowTradeAndEarnCompetitionFields })
  .superRefine(enforceSaveInvariants);
export type UpdateCedeFlowTradeAndEarnCompetitionDto = z.infer<typeof UpdateCedeFlowTradeAndEarnCompetitionDtoSchema>;

export const SaveCedeFlowTradeAndEarnCompetitionDtoSchema = z.union([
  UpdateCedeFlowTradeAndEarnCompetitionDtoSchema,
  CreateCedeFlowTradeAndEarnCompetitionDtoSchema,
]);
export type SaveCedeFlowTradeAndEarnCompetitionDto = z.infer<typeof SaveCedeFlowTradeAndEarnCompetitionDtoSchema>;

// ====== Query params ======

export const GetCedeFlowTradeAndEarnCompetitionByIdQueryParamsSchema = z.object({
  competitionId: z.uuid(),
});
export type GetCedeFlowTradeAndEarnCompetitionByIdQueryParamsDto = z.infer<
  typeof GetCedeFlowTradeAndEarnCompetitionByIdQueryParamsSchema
>;

export const GetCedeFlowTradeAndEarnCompetitionBySlugQueryParamsSchema = z.object({
  slug: z.string().min(1),
});
export type GetCedeFlowTradeAndEarnCompetitionBySlugQueryParamsDto = z.infer<
  typeof GetCedeFlowTradeAndEarnCompetitionBySlugQueryParamsSchema
>;

export const GetCedeFlowTradeAndEarnCompetitionsByPageQueryParamsSchema = z.object({
  // Pagination is 0-indexed on the API (`skip: page * size` in the
  // repository). The CMS list view sends `page=0` for the first page.
  page: z.coerce.number().int().min(0).optional(),
  size: z.coerce.number().int().min(1).max(100).optional(),
  search: z.string().optional(),
});
export type GetCedeFlowTradeAndEarnCompetitionsByPageQueryParamsDto = z.infer<
  typeof GetCedeFlowTradeAndEarnCompetitionsByPageQueryParamsSchema
>;

export const DeleteCedeFlowTradeAndEarnCompetitionQueryParamsSchema = z.object({
  competitionId: z.uuid(),
});
export type DeleteCedeFlowTradeAndEarnCompetitionQueryParamsDto = z.infer<
  typeof DeleteCedeFlowTradeAndEarnCompetitionQueryParamsSchema
>;

// ====== Result schemas ======

export const GetCedeFlowTradeAndEarnCompetitionsByPageResultSchema = z.object({
  data: z.array(CedeFlowTradeAndEarnCompetitionDtoSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(0),
  size: z.number().int().min(1),
});
export type GetCedeFlowTradeAndEarnCompetitionsByPageResultDto = z.infer<
  typeof GetCedeFlowTradeAndEarnCompetitionsByPageResultSchema
>;

export const GroupAggregateSchema = z.object({
  groupSlug: z.string().min(1),
  memberCount: z.number().int().min(1),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
});
export type GroupAggregate = z.infer<typeof GroupAggregateSchema>;

export const GetActiveCedeFlowTradeAndEarnCompetitionsResultSchema = z.object({
  competitions: z.array(CedeFlowTradeAndEarnCompetitionDtoSchema),
  groupAggregates: z.record(z.string(), GroupAggregateSchema).default({}),
});
export type GetActiveCedeFlowTradeAndEarnCompetitionsResultDto = z.infer<
  typeof GetActiveCedeFlowTradeAndEarnCompetitionsResultSchema
>;
